/** Structured birth place as sent to the backend (`lat`/`lon` from GeoNames). */
export interface BirthPlace {
  label: string
  lat: number
  lon: number
}

interface BirthPlaceDraft {
  label: string
  lat?: number
  lon?: number
}

function hasCoords(place: BirthPlaceDraft): place is BirthPlace {
  return (
    Number.isFinite(place.lat) &&
    Number.isFinite(place.lon) &&
    place.lat !== undefined &&
    place.lon !== undefined
  )
}

/**
 * Persist a city pick in quiz answers. Full GeoNames hits become JSON (label +
 * coords); a free-typed label stays a plain string so the combobox can show it.
 */
export function encodeBirthPlace(place: BirthPlaceDraft): string {
  const label = place.label.trim()
  if (!label) return ""
  if (!hasCoords(place)) return label
  return JSON.stringify({ label, lat: place.lat, lon: place.lon })
}

/** Label shown in the city input — never the raw JSON payload. */
export function birthPlaceLabel(value: string | undefined | null): string {
  if (!value) return ""
  const trimmed = value.trim()
  if (!trimmed.startsWith("{")) return trimmed
  try {
    const parsed = JSON.parse(trimmed) as { label?: unknown }
    return typeof parsed.label === "string" ? parsed.label : trimmed
  } catch {
    return trimmed
  }
}

/**
 * Quiz `birthplace` answer → API payload. Returns null when empty or when we
 * only have a label (no coords) — the backend contract requires `lat`/`lon`.
 */
export function parseBirthPlace(value: string | undefined | null): BirthPlace | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { label?: unknown; lat?: unknown; lon?: unknown }
      const label = typeof parsed.label === "string" ? parsed.label.trim() : ""
      const lat = typeof parsed.lat === "number" ? parsed.lat : Number.NaN
      const lon = typeof parsed.lon === "number" ? parsed.lon : Number.NaN
      if (label && Number.isFinite(lat) && Number.isFinite(lon)) {
        return { label, lat, lon }
      }
    } catch {
      /* not JSON — treat as a plain label */
    }
  }

  return null
}
